import { useState, useMemo } from 'react';
import axios from 'axios';
import { X, Eye } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { DatePicker } from '@/components/ui/native-datetime';
import { toast } from 'sonner';
import { formatSubmitError } from '@/utils/formErrors';
import { useNavigate } from 'react-router-dom';
import { EMPLOYEE_NAMES as FALLBACK_NAMES } from '@/lib/workCalculations';
import useDirectory from '@/hooks/useDirectory';
import useUsedDates from '@/hooks/useUsedDates';
import { Req, Opt, labelCls, fieldInput, fieldTextarea, fieldTrigger, btnPrimary, btnSecondary } from '@/components/forms/formKit';
import { API, RECEIPT_REQUIRED_MODES, newLeg, buildInitialState } from './travel/travelModel';
import { TransportLegsSection } from './travel/TransportLegsSection';
import { OtherFeesSection } from './travel/OtherFeesSection';
import { TravelPreview } from './travel/TravelPreview';

export default function TravelExpenseFormModal({ onClose, initialData, submissionId, embedded = false }) {
  const navigate = useNavigate();
  const { employees, employeeNames, departmentNames } = useDirectory();
  const empOptions = employeeNames.length ? employeeNames : FALLBACK_NAMES;
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLegId, setUploadingLegId] = useState(null);
  const [selectedLegIds, setSelectedLegIds] = useState([]);
  const [otherReceiptUploading, setOtherReceiptUploading] = useState(false);

  const [formData, setFormData] = useState(() => buildInitialState(initialData));
  const isEdit = !!submissionId;
  const usedDates = useUsedDates('travel_expense', formData.employee_names, submissionId);

  // ── Derived totals ──
  const transportationFee = useMemo(
    () => formData.transport_legs.reduce((s, l) => s + (parseFloat(l.fare) || 0), 0),
    [formData.transport_legs]
  );
  const totalAmount = useMemo(() => {
    return transportationFee
      + (parseFloat(formData.accommodation_fee) || 0)
      + (parseFloat(formData.daily_allowance) || 0)
      + (parseFloat(formData.meal_fee) || 0)
      + (parseFloat(formData.other_fees) || 0);
  }, [transportationFee, formData.accommodation_fee, formData.daily_allowance, formData.meal_fee, formData.other_fees]);

  // Flatten all receipts across legs + other_receipts for top-level storage (print pages)
  const allReceipts = useMemo(
    () => [
      ...formData.transport_legs.flatMap((l) => (l.receipts || []).map((r) => ({ ...r, leg_mode: l.mode, leg_route: `${l.from || ''}→${l.to || ''}` }))),
      ...(formData.other_receipts || []).map((r) => ({ ...r, leg_mode: 'その他精算項目', leg_route: '' })),
    ],
    [formData.transport_legs, formData.other_receipts]
  );

  const isMultiDay = useMemo(() => {
    if (!formData.travel_start_date || !formData.travel_end_date) return false;
    return formData.travel_end_date > formData.travel_start_date;
  }, [formData.travel_start_date, formData.travel_end_date]);

  // Rows that require but don't have receipts
  const missingReceiptLegs = useMemo(
    () => formData.transport_legs.filter(
      (l) => RECEIPT_REQUIRED_MODES.has(l.mode) && (l.receipts || []).length === 0
    ),
    [formData.transport_legs]
  );

  // ── Field updates ──
  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'employee_names' && Array.isArray(value) && value.length > 0) {
        const first = employees.find((e) => e.name === value[0]);
        if (first && first.department_name && !prev.department) updated.department = first.department_name;
      }
      if (field === 'department') {
        updated.employee_names = (prev.employee_names || []).filter((n) => {
          const emp = employees.find((e) => e.name === n);
          return emp && emp.department_name === value;
        });
      }
      // Auto-clear end_date if it's before start_date
      if (field === 'travel_start_date' && prev.travel_end_date && value > prev.travel_end_date) {
        updated.travel_end_date = '';
      }
      return updated;
    });
  };

  const updateLeg = (id, patch) => {
    setFormData((prev) => ({
      ...prev,
      transport_legs: prev.transport_legs.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    }));
  };

  const addLeg = () => {
    setFormData((prev) => ({ ...prev, transport_legs: [...prev.transport_legs, newLeg()] }));
  };

  const removeLeg = (id) => {
    setFormData((prev) => {
      if (prev.transport_legs.length <= 1) return prev;
      return { ...prev, transport_legs: prev.transport_legs.filter((l) => l.id !== id) };
    });
    setSelectedLegIds((prev) => prev.filter((x) => x !== id));
  };

  const cloneLeg = (leg) => ({
    ...leg,
    id: `leg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    // Don't clone receipts — they belong to the original leg
    receipts: [],
  });

  const copyLeg = (id) => {
    setFormData((prev) => {
      const idx = prev.transport_legs.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copy = cloneLeg(prev.transport_legs[idx]);
      const next = [...prev.transport_legs];
      next.splice(idx + 1, 0, copy);
      return { ...prev, transport_legs: next };
    });
    toast.success('区間を複製しました');
  };

  const toggleLegSelect = (id) => {
    setSelectedLegIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const clearLegSelection = () => setSelectedLegIds([]);

  const bulkCopyLegs = () => {
    if (selectedLegIds.length === 0) return;
    setFormData((prev) => {
      const copies = prev.transport_legs.filter((l) => selectedLegIds.includes(l.id)).map(cloneLeg);
      return { ...prev, transport_legs: [...prev.transport_legs, ...copies] };
    });
    toast.success(`${selectedLegIds.length}件の区間を複製しました`);
    setSelectedLegIds([]);
  };

  const bulkDeleteLegs = () => {
    if (selectedLegIds.length === 0) return;
    setFormData((prev) => {
      // Keep at least one leg
      const remaining = prev.transport_legs.filter((l) => !selectedLegIds.includes(l.id));
      if (remaining.length === 0) {
        toast.error('少なくとも1件の区間が必要です');
        return prev;
      }
      return { ...prev, transport_legs: remaining };
    });
    setSelectedLegIds([]);
  };

  // ── その他精算項目の領収書 ──
  const { accommodation_fee, daily_allowance, meal_fee, other_fees } = formData;
  const otherFeeTotal = useMemo(
    () => [accommodation_fee, daily_allowance, meal_fee, other_fees]
      .reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [accommodation_fee, daily_allowance, meal_fee, other_fees]
  );
  const otherReceiptsRequired = otherFeeTotal > 0;
  const otherReceiptsMissing = otherReceiptsRequired && (formData.other_receipts || []).length === 0;

  const uploadFiles = async (files) => {
    const uploaded = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/receipts/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      uploaded.push(res.data);
    }
    return uploaded;
  };

  const handleOtherReceiptUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setOtherReceiptUploading(true);
    try {
      const uploaded = await uploadFiles(files);
      setFormData((prev) => ({ ...prev, other_receipts: [...(prev.other_receipts || []), ...uploaded] }));
      toast.success(`${uploaded.length}件の領収書をアップロードしました`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || '領収書のアップロードに失敗しました');
    } finally {
      setOtherReceiptUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveOtherReceipt = async (id) => {
    try { await axios.delete(`${API}/receipts/${id}`); } catch (_) { /* ignore */ }
    setFormData((prev) => ({ ...prev, other_receipts: (prev.other_receipts || []).filter((r) => r.id !== id) }));
  };

  const filteredEmpOptions = formData.department
    ? employees.filter((e) => e.department_name === formData.department).map((e) => e.name)
    : empOptions;

  // ── Receipt upload per leg ──
  const handleLegReceiptUpload = async (legId, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingLegId(legId);
    try {
      const uploaded = await uploadFiles(files);
      setFormData((prev) => ({
        ...prev,
        transport_legs: prev.transport_legs.map((l) =>
          l.id === legId ? { ...l, receipts: [...(l.receipts || []), ...uploaded] } : l
        ),
      }));
      toast.success(`${uploaded.length}件の領収書をアップロードしました`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || '領収書のアップロードに失敗しました');
    } finally {
      setUploadingLegId(null);
      e.target.value = '';
    }
  };

  const handleRemoveLegReceipt = async (legId, receiptId) => {
    try { await axios.delete(`${API}/receipts/${receiptId}`); } catch (_) { /* ignore */ }
    setFormData((prev) => ({
      ...prev,
      transport_legs: prev.transport_legs.map((l) =>
        l.id === legId ? { ...l, receipts: l.receipts.filter((r) => r.id !== receiptId) } : l
      ),
    }));
  };

  // ── Submit ──
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const primary = (formData.employee_names || [])[0];
      const primaryEmp = primary ? employees.find((e) => e.name === primary) : null;
      const submitData = {
        ...formData,
        employee_name: formData.employee_names.join('、'),
        // Primary applicant's employee_code + id_card (used by the print sheet
        // 請求者 box). Derived from the employees directory at submit time.
        employee_code: primaryEmp?.code || formData.employee_code || '',
        id_card: primaryEmp?.id_card || formData.id_card || '',
        // Derived back-compat fields
        transportation_fee: transportationFee,
        total_amount: totalAmount,
        // Convenience: first leg's route fills legacy fields
        departure: formData.transport_legs[0]?.from || '',
        destination: formData.transport_legs[formData.transport_legs.length - 1]?.to || '',
        travel_date: formData.travel_start_date,
        receipts: allReceipts,
      };
      if (isEdit) {
        await axios.put(`${API}/forms/submissions/${submissionId}`, { form_data: submitData });
        toast.success('申請を更新しました');
        if (onClose) onClose();
        setTimeout(() => navigate(`/submission/${submissionId}`), 300);
      } else {
        await axios.post(`${API}/forms/submit`, submitData);
        toast.success('登録しました — 履歴に追加されました');
        // Reset for the next submission instead of navigating away.
        setFormData(buildInitialState(undefined));
        setShowPreview(false);
      }
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(formatSubmitError(error, isEdit));
    } finally {
      setSubmitting(false);
    }
  };

  const canPreview = formData.employee_names.length > 0
    && formData.department
    && formData.travel_start_date
    && formData.purpose
    && formData.transport_legs.every((l) => l.from && l.to && l.mode)
    && missingReceiptLegs.length === 0
    && !otherReceiptsMissing;

  return (
    embedded ? (
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 w-full" data-testid="travel-expense-edit">
        <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-5 flex items-center justify-between z-20 rounded-t-2xl">
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900">出張旅費精算請求書 の編集</h2>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-500 hover:text-zinc-900">
              <X size={22} />
            </button>
          )}
        </div>
        {renderBody()}
      </div>
    ) : (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-zinc-900/70 pointer-events-auto">
      <div className="bg-white rounded-lg shadow-xl border border-zinc-200 w-full max-w-4xl max-h-[calc(100vh-7rem)] overflow-y-auto mt-20 sm:mt-24 mx-4 mb-4">
        <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-5 flex items-center justify-between z-20">
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900">出張旅費精算請求書</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-500 hover:text-zinc-900">
            <X size={24} />
          </button>
        </div>
        {renderBody()}
      </div>
    </div>
    )
  );

  function renderBody() {
    if (showPreview) {
      return (
        <TravelPreview
          formData={formData}
          transportationFee={transportationFee}
          totalAmount={totalAmount}
          submitting={submitting}
          isEdit={isEdit}
          onBack={() => setShowPreview(false)}
          onSubmit={handleSubmit}
        />
      );
    }
    return (
      <div className="p-6 sm:p-8 space-y-6">
        {/* Basic info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="department" className={labelCls}>所属部署<Req /></Label>
            {departmentNames.length > 0 ? (
              <Select value={formData.department} onValueChange={(val) => handleChange('department', val)}>
                <SelectTrigger data-testid="select-department" className={`mt-1.5 ${fieldTrigger}`}>
                  <SelectValue placeholder="所属部署を選択" />
                </SelectTrigger>
                <SelectContent>
                  {departmentNames.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="department"
                value={formData.department}
                onChange={(e) => handleChange('department', e.target.value)}
                className={`mt-1.5 ${fieldInput}`}
                required
              />
            )}
          </div>
          <div>
            <Label className={labelCls}>氏名<Req /><span className="ml-2 text-xs font-normal text-zinc-500">複数選択可</span></Label>
            <MultiSelect
              options={filteredEmpOptions}
              selected={formData.employee_names}
              onChange={(names) => handleChange('employee_names', names)}
              placeholder={formData.department ? '氏名を選択してください（複数可）' : 'まず所属部署を選択'}
              testId="multi-select-employee-names"
              disabled={!formData.department && departmentNames.length > 0}
            />
          </div>
        </div>

        {/* Travel period */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="travel_start_date" className={labelCls}>出張開始日<Req /></Label>
            <DatePicker
              id="travel_start_date"
              testId="input-travel-start-date"
              value={formData.travel_start_date}
              onChange={(v) => handleChange('travel_start_date', v)}
              disabledDates={usedDates}
              required
            />
          </div>
          <div>
            <Label htmlFor="travel_end_date" className={labelCls}>出張終了日<Opt /></Label>
            <DatePicker
              id="travel_end_date"
              testId="input-travel-end-date"
              value={formData.travel_end_date}
              min={formData.travel_start_date}
              onChange={(v) => handleChange('travel_end_date', v)}
              disabledDates={usedDates}
            />
            <p className="text-xs text-zinc-500 mt-1">複数日の場合のみ入力（{isMultiDay ? '複数日出張' : '日帰り'}）</p>
          </div>
        </div>

        <TransportLegsSection
          legs={formData.transport_legs}
          selectedIds={selectedLegIds}
          isMultiDay={isMultiDay}
          travelStartDate={formData.travel_start_date}
          travelEndDate={formData.travel_end_date}
          uploadingLegId={uploadingLegId}
          transportationFee={transportationFee}
          onAdd={addLeg}
          onBulkCopy={bulkCopyLegs}
          onBulkDelete={bulkDeleteLegs}
          onClearSelection={clearLegSelection}
          onToggleSelect={toggleLegSelect}
          onCopy={copyLeg}
          onRemove={removeLeg}
          onUpdate={updateLeg}
          onReceiptUpload={handleLegReceiptUpload}
          onReceiptRemove={handleRemoveLegReceipt}
        />

        {/* Purpose */}
        <div>
          <Label htmlFor="purpose" className={labelCls}>用件・目的<Req /></Label>
          <Textarea
            id="purpose"
            value={formData.purpose}
            onChange={(e) => handleChange('purpose', e.target.value)}
            className={`mt-1.5 ${fieldTextarea} min-h-[80px]`}
            placeholder="出張の目的を記載してください"
            required
          />
        </div>

        <OtherFeesSection
          formData={formData}
          totalAmount={totalAmount}
          otherReceiptsRequired={otherReceiptsRequired}
          otherReceiptsMissing={otherReceiptsMissing}
          uploading={otherReceiptUploading}
          onChange={handleChange}
          onUpload={handleOtherReceiptUpload}
          onRemoveReceipt={handleRemoveOtherReceipt}
        />

        {missingReceiptLegs.length > 0 && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {missingReceiptLegs.length}件の区間で領収書が未アップロードです（{missingReceiptLegs.map((l) => l.mode).join('、')}）。アップロード後にプレビューできます。
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} className={btnSecondary}>キャンセル</Button>
          <Button
            onClick={() => setShowPreview(true)}
            disabled={!canPreview}
            data-testid="btn-preview"
            className={btnPrimary}
          >
            <Eye size={18} className="mr-2" />
            プレビュー
          </Button>
        </div>
      </div>
    );
  }
}
